import React, { Fragment, useState } from 'react';
import { Box, Breadcrumbs, Button, Typography } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useTranslation } from 'react-i18next';
import { IHeader, TButtonLink, TRenderButton } from './types';
import {
    CREATE_PERMISSION,
    READ_PERMISSION,
    UPDATE_PERMISSION,
} from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import useAuth from 'src/hooks/useAuth';
import {
    CREATE_ROUTE,
    EDIT_ROUTE,
    MANAGEMENT_ROUTE,
} from 'src/constants/routing';
import { ArrowLeft, BookOpen, Delete, Edit3, Plus } from 'react-feather';
import { makeStyles, Theme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import DeleteDialog from '../SimpleDialog';
import { useSnackbar } from 'notistack';
import { useHistory, Link } from 'react-router-dom';
import { CapitalizeNames } from 'src/utils/capitalize';

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        height: 56,
        textTransform: 'capitalize',
        margin: 0,
    },
    error: {
        color: theme.palette.common.white,
        borderColor: theme.palette.error.main,
        textTransform: 'capitalize',
        '&:hover': {
            borderColor: theme.palette.error.main,
            backgroundColor: alpha(theme.palette.error.main, 0.8),
        },
        backgroundColor: theme.palette.error.main,
    },
    mainContainer: {
        height: 85,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
    },
    breadcrumbsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        flexGrow: 1,
    },
    buttonsContainer: {
        display: 'flex',
        gap: 10,
    },
}));

export const Header = ({
    module,
    route,
    crud,
    id,
    permission,
    title,
    onDelete,
}: IHeader) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const classes = useStyles();
    const [open, setOpen] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();

    const shouldRenderButton = ({ permissionType }: TRenderButton) => {
        if (crud && !crud[permissionType]) return false;

        if (permission) {
            return isPermissionGranted({
                permissions: user.permissions || {},
                permission: permission,
                crud: permissionType,
            });
        } else return true;
    };

    const handleDelete = async (value: string) => {
        if (onDelete && id) {
            if (value === 'yes') {
                await onDelete(id);
                enqueueSnackbar(
                    t(`SnackBar.${CapitalizeNames(module)}Deleted`),
                    {
                        variant: 'error',
                    }
                );
                history.push(`/${MANAGEMENT_ROUTE}/${route}`);
            }
            setOpen(false);
        }
    };

    const handleOpen = () => setOpen(true);

    const createButtonProps = (
        to: string,
        icon: React.ReactNode
    ): TButtonLink => ({
        color: 'primary',
        variant: 'contained',
        startIcon: icon,
        component: Link,
        className: classes.button,
        to,
    });

    return (
        <Fragment>
            <DeleteDialog open={open} onClose={handleDelete} />
            <Box
                className={classes.mainContainer}
                style={{ padding: '0px 10px' }}
            >
                <Box className={classes.breadcrumbsContainer}>
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                    >
                        <Typography
                            variant="h4"
                            color="textPrimary"
                            style={{ fontWeight: 400 }}
                        >
                            {t('management')}
                        </Typography>
                        <Typography variant="body1" color="textPrimary">
                            {t(`${module}.module`)}
                        </Typography>
                    </Breadcrumbs>
                    {title && (
                        <Typography variant="h4" color="textPrimary">
                            {title}
                        </Typography>
                    )}
                </Box>
                <Box className={classes.buttonsContainer}>
                    {crud?.goBack && (
                        <Button
                            {...createButtonProps(
                                `/${MANAGEMENT_ROUTE}/${route}`,
                                <ArrowLeft />
                            )}
                        >
                            {t(`${module}.GoBack`)}
                        </Button>
                    )}

                    {shouldRenderButton({
                        permissionType: CREATE_PERMISSION,
                    }) && (
                        <Button
                            {...createButtonProps(
                                `/${CREATE_ROUTE}/${module}`,
                                <Plus />
                            )}
                        >
                            {t(`${module}.create`)}
                        </Button>
                    )}

                    {id &&
                        shouldRenderButton({
                            permissionType: READ_PERMISSION,
                        }) && (
                            <Button
                                {...createButtonProps(
                                    `/${MANAGEMENT_ROUTE}/${route}/${id}`,
                                    <BookOpen />
                                )}
                            >
                                {t(`${module}.details`)}
                            </Button>
                        )}

                    {id &&
                        shouldRenderButton({
                            permissionType: UPDATE_PERMISSION,
                        }) && (
                            <Button
                                {...createButtonProps(
                                    `/${MANAGEMENT_ROUTE}/${route}/${id}/${EDIT_ROUTE}`,
                                    <Edit3 />
                                )}
                            >
                                {t(`${module}.edit`)}
                            </Button>
                        )}

                    {id && onDelete && crud?.delete && (
                        <Button
                            {...createButtonProps(
                                `/${MANAGEMENT_ROUTE}/${route}/${id}`,
                                <Delete />
                            )}
                            onClick={handleOpen}
                            className={classes.error}
                        >
                            {t(`${module}.delete`)}
                        </Button>
                    )}
                </Box>
            </Box>
        </Fragment>
    );
};
